import { useEffect, useState } from 'react';
import { ManageAccounts, Cancel } from '@mui/icons-material';

// material-ui
import {
    Box,
    Button,
    Grid,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    TextField,
    Checkbox,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';

import { deepPurple } from '@mui/material/colors';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { axiosInstance } from 'utils/axios';
import { toast } from 'react-toastify';

const columns = [
    { id: 'name', label: 'Package Name', minWidth: 150 },
    { id: 'packageTypeName', label: 'Package Type', minWidth: 150 },
    { id: 'payoutPercentage', label: 'Historical Rate of Return (%)', minWidth: 150 },
    { id: 'durationDays', label: 'Duration Days', minWidth: 120 },
    { id: 'minContributionAmount', label: 'Min Contribution', minWidth: 120 },
    { id: 'maxContributionAmount', label: 'Max Contribution', minWidth: 120 },
    { id: 'active', label: 'Active', minWidth: 80 }
];

const renderTableCell = (value) => {
    if (value === 'active') {
        return <span style={{ padding: '8px 12px', backgroundColor: 'green', color: 'white', borderRadius: '5px', fontSize: '12px' }}>Active</span>;
    }
    if (value === 'inactive') {
        return <span style={{ padding: '8px 12px', backgroundColor: 'red', color: 'white', borderRadius: '5px', fontSize: '12px' }}>Inactive</span>;
    }
    return value;
};

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 800,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const PackageItemRecords = () => {
    const token = useSelector(selectCurrentToken);
    
    // States
    const [packages, setPackages] = useState([]);
    const [packageTypes, setPackageTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Toast Alerts
    const warningAlert = (message) => toast.warning(message, { position: toast.POSITION.TOP_CENTER });
    const successAlert = (message) => toast.success(message, { position: toast.POSITION.TOP_CENTER });
    const errorAlert = (message) => toast.error(message, { position: toast.POSITION.TOP_CENTER });

    // Fetch Package Types
    const fetchPackageTypes = async () => {
        try {
            const res = await axiosInstance.get('/Backend/PackageTypes/All', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setPackageTypes(res.data);
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Fetch Packages (Static Dates as requested)
    const fetchPackages = async () => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get('/Package/All?createdDateFrom=2026-01-01&createdDateTo=2026-01-01', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.status === 200) {
                setPackages(res.data);
            } else {
                errorAlert('Failed to fetch packages.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPackageTypes();
        fetchPackages();
    }, []);

    // Helper: Map Package Type ID to Name for the table
    const getPackageTypeName = (id) => {
        const type = packageTypes.find(t => t.id === id);
        return type ? type.name : '-';
    };

    // --- INSERT PACKAGE MODAL ---
    const InsertPackage = () => {
        const [open, setOpen] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Form States
        const [name, setName] = useState('');
        const [payoutPercentage, setPayoutPercentage] = useState('');
        const [active, setActive] = useState(true);
        const [durationDays, setDurationDays] = useState('');
        const [minPayout, setMinPayout] = useState('');
        const [maxPayout, setMaxPayout] = useState('');
        const [minContribution, setMinContribution] = useState('');
        const [maxContribution, setMaxContribution] = useState('');
        const [packageTypeID, setPackageTypeID] = useState('');

        const handleSubmit = async () => {
            if (!name || !packageTypeID || !payoutPercentage || !durationDays) {
                warningAlert('Please fill all required inputs.');
                return;
            }
            setIsSubmitting(true);
            try {
                const res = await axiosInstance.post('/Package', {
                    name: name,
                    payoutPercentage: parseFloat(payoutPercentage),
                    active: active,
                    durationDays: parseInt(durationDays),
                    minPayoutPercentage: parseFloat(minPayout || 0),
                    maxPayoutPercentage: parseFloat(maxPayout || 0),
                    minContributionAmount: parseFloat(minContribution || 0),
                    maxContributionAmount: parseFloat(maxContribution || 0),
                    packageTypeID: parseInt(packageTypeID)
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.status === 200 || res.status === 201) {
                    successAlert('Package created successfully.');
                    fetchPackages();
                    setOpen(false);
                }
            } catch (error) {
                errorAlert('Failed to create package.');
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <>
                <Button variant="contained" onClick={() => setOpen(true)} sx={{ bgcolor: deepPurple[500], mb: 2 }}>
                    Insert Package
                </Button>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <Box sx={style}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h3">Insert Package</Typography>
                            <Cancel onClick={() => setOpen(false)} sx={{ cursor: 'pointer' }} />
                        </Box>
                        <Grid container spacing={2} sx={{ height: '400px', overflowY: 'auto', p: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography>Package Name *</Typography>
                                <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Package Type *</Typography>
                                <Select fullWidth size="small" value={packageTypeID} onChange={(e) => setPackageTypeID(e.target.value)}>
                                    <MenuItem value="" disabled>Select Type</MenuItem>
                                    {packageTypes.map(pt => <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>)}
                                </Select>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Historical Rate of Return (%) *</Typography>
                                <TextField fullWidth type="number" size="small" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Duration Days *</Typography>
                                <TextField fullWidth type="number" size="small" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Min Payout %</Typography>
                                <TextField fullWidth type="number" size="small" value={minPayout} onChange={(e) => setMinPayout(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Max Payout %</Typography>
                                <TextField fullWidth type="number" size="small" value={maxPayout} onChange={(e) => setMaxPayout(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Min Contribution Amount</Typography>
                                <TextField fullWidth type="number" size="small" value={minContribution} onChange={(e) => setMinContribution(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Max Contribution Amount</Typography>
                                <TextField fullWidth type="number" size="small" value={maxContribution} onChange={(e) => setMaxContribution(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Active</Typography>
                                <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />
                            </Grid>
                        </Grid>
                        <Button fullWidth variant="contained" sx={{ bgcolor: deepPurple[500], mt: 3 }} onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={24} /> : "Create Package"}
                        </Button>
                    </Box>
                </Modal>
            </>
        );
    };

    // --- UPDATE PACKAGE MODAL ---
    const UpdatePackageModal = ({ packageDetails }) => {
        const [open, setOpen] = useState(false);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Pre-filled Form States
        const [name, setName] = useState(packageDetails.name || '');
        const [payoutPercentage, setPayoutPercentage] = useState(packageDetails.payoutPercentage || '');
        const [active, setActive] = useState(packageDetails.active);
        const [durationDays, setDurationDays] = useState(packageDetails.durationDays || '');
        const [minPayout, setMinPayout] = useState(packageDetails.minPayoutPercentage || '');
        const [maxPayout, setMaxPayout] = useState(packageDetails.maxPayoutPercentage || '');
        const [minContribution, setMinContribution] = useState(packageDetails.minContributionAmount || '');
        const [maxContribution, setMaxContribution] = useState(packageDetails.maxContributionAmount || '');
        const [packageTypeID, setPackageTypeID] = useState(packageDetails.packageTypeID || '');

        const handleSubmit = async () => {
            if (!name || !packageTypeID || !payoutPercentage || !durationDays) {
                warningAlert('Please fill all required inputs.');
                return;
            }
            setIsSubmitting(true);
            try {
                const res = await axiosInstance.put('/Package', {
                    packageID: packageDetails.id,
                    name: name,
                    payoutPercentage: parseFloat(payoutPercentage),
                    active: active,
                    durationDays: parseInt(durationDays),
                    minPayoutPercentage: parseFloat(minPayout || 0),
                    maxPayoutPercentage: parseFloat(maxPayout || 0),
                    minContributionAmount: parseFloat(minContribution || 0),
                    maxContributionAmount: parseFloat(maxContribution || 0),
                    packageTypeID: parseInt(packageTypeID)
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.status === 200) {
                    successAlert('Package updated successfully.');
                    fetchPackages();
                    setOpen(false);
                }
            } catch (error) {
                errorAlert('Failed to update package.');
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <>
                <Button onClick={() => setOpen(true)} sx={{ minWidth: 0, p: 1, color: deepPurple[500] }}>
                    <ManageAccounts />
                </Button>
                <Modal open={open} onClose={() => setOpen(false)}>
                    <Box sx={style}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h3">Update Package</Typography>
                            <Cancel onClick={() => setOpen(false)} sx={{ cursor: 'pointer' }} />
                        </Box>
                        <Grid container spacing={2} sx={{ height: '400px', overflowY: 'auto', p: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography>Package Name *</Typography>
                                <TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Package Type *</Typography>
                                <Select fullWidth size="small" value={packageTypeID} onChange={(e) => setPackageTypeID(e.target.value)}>
                                    <MenuItem value="" disabled>Select Type</MenuItem>
                                    {packageTypes.map(pt => <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>)}
                                </Select>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Historical Rate of Return (%) *</Typography>
                                <TextField fullWidth type="number" size="small" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Duration Days *</Typography>
                                <TextField fullWidth type="number" size="small" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Min Payout %</Typography>
                                <TextField fullWidth type="number" size="small" value={minPayout} onChange={(e) => setMinPayout(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Max Payout %</Typography>
                                <TextField fullWidth type="number" size="small" value={maxPayout} onChange={(e) => setMaxPayout(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Min Contribution Amount</Typography>
                                <TextField fullWidth type="number" size="small" value={minContribution} onChange={(e) => setMinContribution(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Max Contribution Amount</Typography>
                                <TextField fullWidth type="number" size="small" value={maxContribution} onChange={(e) => setMaxContribution(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography>Active</Typography>
                                <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />
                            </Grid>
                        </Grid>
                        <Button fullWidth variant="contained" sx={{ bgcolor: deepPurple[500], mt: 3 }} onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={24} /> : "Update Package"}
                        </Button>
                    </Box>
                </Modal>
            </>
        );
    };

    // --- TABLE COMPONENT ---
    function PackagesTable() {
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        return (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.id} style={{ minWidth: column.minWidth, fontWeight: 'bold' }}>
                                        {column.label}
                                    </TableCell>
                                ))}
                                <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {packages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow hover key={row.id}>
                                    {columns.map((column) => {
                                        let value = row[column.id];
                                        if (column.id === 'active') value = value ? 'active' : 'inactive';
                                        if (column.id === 'packageTypeName') value = getPackageTypeName(row.packageTypeID);
                                        return <TableCell key={column.id}>{renderTableCell(value)}</TableCell>;
                                    })}
                                    <TableCell>
                                        <UpdatePackageModal packageDetails={row} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={packages.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                />
            </Paper>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <InsertPackage />
            </Grid>
            <Grid item xs={12}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <PackagesTable />
                )}
            </Grid>
        </Grid>
    );
};

export default PackageItemRecords;